import { managedSlugReadsImages, NEUCLEOS_AUTO_SLUG } from "@cleo/contracts";
import type { ChatAttachment, ModelCatalog } from "@cleo/contracts";

import type { ManagedChooserRow } from "./modelChooserRoot";

const TIER_LABELS = { fast: "Fast", standard: "Standard", deep: "Deep" } as const;

/** The picker's mark on a managed model that cannot read this conversation's images. */
export const MANAGED_IMAGES_UNREADABLE = "Cannot read this conversation's images";

export { managedSlugReadsImages };

/**
 * The images a conversation holds: every image attached to a person's
 * message is sent again with each later turn, so a model that cannot read
 * images cannot answer anything more in this conversation.
 */
export function conversationImageCount(
  messages: ReadonlyArray<{
    readonly role: string;
    readonly attachments?: ReadonlyArray<ChatAttachment> | undefined;
  }>,
): number {
  let count = 0;
  for (const message of messages) {
    if (message.role !== "user") continue;
    for (const attachment of message.attachments ?? []) if (attachment.type === "image") count += 1;
  }
  return count;
}

/** How a managed choice is named to the person. */
export function managedChoiceLabel(slug: string, fallback?: string): string {
  if (slug === NEUCLEOS_AUTO_SLUG) return "Neucleos Auto";
  const tier = /^neucleos\/(fast|standard|deep)$/.exec(slug)?.[1] as
    | keyof typeof TIER_LABELS
    | undefined;
  if (tier) return `Neucleos ${TIER_LABELS[tier]}`;
  return fallback ?? slug.slice(slug.indexOf("/") + 1);
}

/** At most this many other models are offered when no Neucleos tier can read images. */
export const MAX_OTHER_IMAGE_MODELS = 2;

export interface ManagedImageInputRefusal {
  /** Short reason, for the Send button's label. */
  readonly reason: string;
  /** What the person reads above the composer. */
  readonly message: string;
  /**
   * The choices offered: the Neucleos tiers that can read images; only when
   * none can, at most MAX_OTHER_IMAGE_MODELS other models, in picker order.
   */
  readonly capable: ReadonlyArray<ManagedChooserRow & { readonly name: string }>;
  /** More models than those offered can read images; the model list has them. */
  readonly moreInModelList: boolean;
}

/**
 * Before Send: the chosen managed model cannot read the images this
 * conversation holds or this message attaches. The World would refuse the
 * turn (and every later one while it stays on this model); it never drops
 * the images or picks another model, so the person chooses. null when the
 * choice can read images, nothing holds an image, or the World does not say.
 */
export function managedImageInputRefusal(input: {
  readonly instanceId: string;
  readonly slug: string;
  readonly catalog: ModelCatalog | null;
  /** Images already in this conversation's messages. */
  readonly heldImages: number;
  /** Images attached to the message being written. */
  readonly draftImages: number;
  /** The chooser's managed rows (resolveModelChooserRoot: neucleos, then frontier). */
  readonly rows: ReadonlyArray<ManagedChooserRow>;
  /** A row this conversation cannot take anyway (the picker hides it). */
  readonly isWithheld?: (row: ManagedChooserRow) => boolean;
  /**
   * The name the model list shows for a row that is not a Neucleos tier
   * (its display name); the row's own label when absent.
   */
  readonly nameOf?: (row: ManagedChooserRow) => string | null | undefined;
}): ManagedImageInputRefusal | null {
  if (input.instanceId !== "neucleos") return null;
  if (input.heldImages + input.draftImages === 0) return null;
  if (managedSlugReadsImages(input.catalog, input.slug) !== false) return null;
  const name = managedChoiceLabel(input.slug);
  const readers = input.rows.filter(
    (row) =>
      row.instanceId === "neucleos" &&
      row.slug !== input.slug &&
      !input.isWithheld?.(row) &&
      managedSlugReadsImages(input.catalog, row.slug) === true,
  );
  const isTier = (row: ManagedChooserRow) => /^neucleos\/(fast|standard|deep|auto)$/.test(row.slug);
  const tiers = readers.filter(isTier);
  const others = readers.filter((row) => !isTier(row));
  const capable =
    tiers.length > 0
      ? tiers.map((row) => ({ ...row, name: managedChoiceLabel(row.slug) }))
      : others.slice(0, MAX_OTHER_IMAGE_MODELS).map((row) => ({
          ...row,
          name: input.nameOf?.(row) || managedChoiceLabel(row.slug, row.label),
        }));
  const moreInModelList = tiers.length === 0 && others.length > capable.length;
  const what =
    input.heldImages > 0
      ? `${name} cannot read the ${input.heldImages === 1 ? "image" : "images"} in this conversation.`
      : `${name} cannot read images.`;
  const next =
    capable.length > 0
      ? input.heldImages > 0
        ? " Choose a model that can to continue."
        : " Choose a model that can, or remove the image."
      : input.heldImages > 0
        ? " No model this World offers you can read images; continue in a new conversation."
        : " No model this World offers you can read images; remove the image to send.";
  return {
    reason: `${name} cannot read images`,
    message: what + next,
    capable,
    moreInModelList,
  };
}
